
import React, { useState } from 'react';
import { User as UserType } from '../types';
import { User, LogOut, Save, Trash2, AlertTriangle, Eye, Link, CheckCircle } from 'lucide-react';
import { db } from '../services/db';

interface SettingsProps {
  user: UserType;
  onLogout: () => void;
  onUserUpdate: (user: UserType) => void;
  onDeleteAccount: () => void;
}

const Settings: React.FC<SettingsProps> = ({ user, onLogout, onUserUpdate, onDeleteAccount }) => {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [hourlyRate, setHourlyRate] = useState(user.hourlyRate);
  const [webhookUrl, setWebhookUrl] = useState(user.webhookUrl || '');
  const [largeTextMode, setLargeTextMode] = useState(user.largeTextMode || false);
  const [autoMarkPaid, setAutoMarkPaid] = useState(user.autoMarkPaid || false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteStage, setDeleteStage] = useState<'initial' | 'confirm'>('initial');

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      onUserUpdate({ ...user, name, email, hourlyRate, webhookUrl, largeTextMode, autoMarkPaid });
      setIsSaving(false);
    }, 600);
  };

  const handleToggle = (key: 'largeTextMode' | 'autoMarkPaid') => {
    let next: Partial<UserType> = {};
    if (key === 'largeTextMode') {
      const newVal = !largeTextMode;
      setLargeTextMode(newVal);
      next.largeTextMode = newVal;
    } else {
      const newVal = !autoMarkPaid;
      setAutoMarkPaid(newVal);
      next.autoMarkPaid = newVal;
    }
    onUserUpdate({ ...user, ...next });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">
      <header>
        <h1 className="text-2xl font-black text-slate-900 tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 font-medium">Manage your professional profile and app behavior.</p>
      </header>

      <div className="grid grid-cols-1 gap-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 flex items-center justify-between gap-4 border-b border-slate-50">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
                <User size={20} />
              </div>
              <div>
                <h3 className="font-black text-slate-900 text-sm tracking-tight">Identity & Billing</h3>
                <p className="text-slate-400 text-xs font-medium">Core profile information.</p>
              </div>
            </div>
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl font-bold text-xs transition flex items-center gap-2 disabled:opacity-50 active:scale-95 shadow-sm"
            >
              {isSaving ? <Save size={14} className="animate-spin" /> : <Save size={14} />} 
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/20">
            <div className="space-y-1.5">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Full Name</label>
              <input 
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold focus:border-blue-500 outline-none transition"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Email Address</label>
              <input 
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold focus:border-blue-500 outline-none transition"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Default Global Hourly Rate ($)</label>
              <input 
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold focus:border-blue-500 outline-none transition"
              />
            </div>
          </div>
        </div>

        {/* Automation Config */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 flex items-center gap-4 border-b border-slate-50">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              <Link size={20} />
            </div>
            <div>
              <h3 className="font-black text-slate-900 text-sm tracking-tight">Automation</h3>
              <p className="text-slate-400 text-xs font-medium">Webhook for PDF and email delivery.</p>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-1.5">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">n8n Webhook Endpoint</label>
              <input 
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://your-n8n-instance.com/webhook/..."
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:border-blue-500 outline-none transition"
              />
            </div>
            
            <button 
              onClick={() => handleToggle('autoMarkPaid')}
              className="w-full flex items-center justify-between p-4 bg-slate-50/50 rounded-xl border border-slate-100 hover:bg-slate-50 transition"
            >
              <div className="text-left flex items-center gap-3">
                <div className={`p-2 rounded-lg ${autoMarkPaid ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                  <CheckCircle size={16} />
                </div>
                <div>
                  <p className="font-bold text-sm text-slate-800">Auto-Finalize Payments</p>
                  <p className="text-[10px] text-slate-500 font-medium">Mark invoices as "PAID" immediately when closing.</p>
                </div>
              </div>
              <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${autoMarkPaid ? 'bg-green-600' : 'bg-slate-300'}`}>
                <div className={`w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${autoMarkPaid ? 'translate-x-6' : 'translate-x-0'}`}></div>
              </div>
            </button>
            
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">Automation is required for "Close & Send" functionality.</p>
          </div>
        </div>

        {/* Accessibility Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 flex items-center gap-4 border-b border-slate-50">
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
              <Eye size={20} />
            </div>
            <div>
              <h3 className="font-black text-slate-900 text-sm tracking-tight">Accessibility</h3>
              <p className="text-slate-400 text-xs font-medium">User interface preferences.</p>
            </div>
          </div>
          <div className="p-6">
            <button 
              onClick={() => handleToggle('largeTextMode')}
              className="w-full flex items-center justify-between p-4 bg-slate-50/50 rounded-xl border border-slate-100 hover:bg-slate-50 transition"
            >
              <div className="text-left">
                <p className="font-bold text-sm text-slate-800">High Visibility Mode</p>
                <p className="text-[11px] text-slate-500 font-medium">Increases font sizes across the entire application.</p>
              </div>
              <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${largeTextMode ? 'bg-blue-600' : 'bg-slate-300'}`}>
                <div className={`w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${largeTextMode ? 'translate-x-6' : 'translate-x-0'}`}></div>
              </div>
            </button>
          </div>
        </div>

        {/* Security & Logout */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={onLogout}
            className="flex-1 bg-white border border-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold text-xs hover:bg-slate-50 transition flex items-center justify-center gap-2"
          >
            <LogOut size={16} /> Sign Out
          </button>
          <button 
            onClick={() => setDeleteStage(deleteStage === 'initial' ? 'confirm' : 'initial')}
            className={`flex-1 px-6 py-3 rounded-xl font-bold text-xs transition flex items-center justify-center gap-2 border ${
              deleteStage === 'confirm' ? 'bg-red-600 text-white border-red-700' : 'bg-red-50 text-red-600 border-red-100'
            }`}
          >
            <Trash2 size={16} /> {deleteStage === 'confirm' ? 'Confirm Permanent Deletion' : 'Delete Account'}
          </button>
        </div>

        {deleteStage === 'confirm' && (
          <div className="bg-red-50 border border-red-200 p-6 rounded-2xl animate-in slide-in-from-top-4 space-y-4">
            <div className="flex items-center gap-3 text-red-700">
              <AlertTriangle size={20} strokeWidth={3} />
              <p className="text-xs font-black uppercase tracking-widest">Final Security Confirmation</p>
            </div>
            <p className="text-xs text-red-700 font-medium leading-relaxed">
              This will permanently purge your account from this browser.
              <strong> This cannot be reversed.</strong>
            </p>
            <div className="flex gap-3">
              <button 
                onClick={onDeleteAccount}
                className="flex-1 py-3 bg-red-700 text-white font-black text-xs rounded-xl hover:bg-red-800 transition shadow-lg shadow-red-500/10"
              >
                PURGE ALL DATA
              </button>
              <button 
                onClick={() => setDeleteStage('initial')}
                className="flex-1 py-3 bg-white text-red-700 border border-red-200 font-black text-xs rounded-xl hover:bg-slate-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
